import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { BrowserProvider, Contract, parseUnits } from "ethers";
import { toast } from "sonner";
import { api } from "@/lib/api";

const WalletContext = createContext(null);
export const useWallet = () => useContext(WalletContext);

const POLYGON_HEX = "0x89"; // 137

const USDC_ABI = [
  "function transfer(address to, uint256 value) returns (bool)",
  "function approve(address spender, uint256 value) returns (bool)",
  "function decimals() view returns (uint8)",
  "function balanceOf(address) view returns (uint256)",
];

const ERC721_ABI = [
  "function registerSector(uint256 tokenId)",
  "function price() view returns (uint256)",
  "function isRegistered(uint256 tokenId) view returns (bool)",
];

export function WalletProvider({ children }) {
  const [account, setAccount] = useState(null);
  const [profile, setProfile] = useState(null);
  const [settings, setSettings] = useState(null);
  const [connecting, setConnecting] = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
    const saved = localStorage.getItem("portal_wallet");
    if (saved) {
      setAccount(saved);
      api.get(`/explorers/${saved}`).then((r) => setProfile(r.data)).catch(() => {});
    }
  }, []);

  useEffect(() => {
    if (!window.ethereum) return;
    const handler = (accts) => {
      if (!accts || accts.length === 0) disconnect();
      else loginWith(accts[0]);
    };
    window.ethereum.on?.("accountsChanged", handler);
    return () => window.ethereum.removeListener?.("accountsChanged", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loginWith = useCallback(async (addr) => {
    const lower = addr.toLowerCase();
    setAccount(lower);
    localStorage.setItem("portal_wallet", lower);
    try {
      await api.post("/explorers/connect", { wallet_address: lower });
      const r = await api.get(`/explorers/${lower}`);
      setProfile(r.data);
    } catch (e) {
      console.error(e);
    }
  }, []);

  const ensurePolygon = async () => {
    const eth = window.ethereum;
    const chainId = await eth.request({ method: "eth_chainId" });
    if (chainId !== POLYGON_HEX) {
      try {
        await eth.request({ method: "wallet_switchEthereumChain", params: [{ chainId: POLYGON_HEX }] });
      } catch (err) {
        if (err.code === 4902) {
          await eth.request({
            method: "wallet_addEthereumChain",
            params: [{
              chainId: POLYGON_HEX,
              chainName: "Polygon Mainnet",
              rpcUrls: ["https://polygon-rpc.com"],
              nativeCurrency: { name: "Polygon", symbol: "POL", decimals: 18 },
              blockExplorerUrls: ["https://polygonscan.com"],
            }],
          });
        } else throw err;
      }
    }
  };

  const connect = useCallback(async () => {
    if (!window.ethereum) {
      toast.error("MetaMask not detected", { description: "Install MetaMask to authenticate as an explorer." });
      window.open("https://metamask.io/download/", "_blank");
      return;
    }
    setConnecting(true);
    try {
      const provider = new BrowserProvider(window.ethereum);
      const accts = await provider.send("eth_requestAccounts", []);
      await loginWith(accts[0]);
      toast.success("Wallet authenticated", { description: "Explorer profile linked." });
    } catch (e) {
      toast.error("Connection failed", { description: e.message });
    } finally {
      setConnecting(false);
    }
  }, [loginWith]);

  const disconnect = useCallback(() => {
    setAccount(null);
    setProfile(null);
    localStorage.removeItem("portal_wallet");
  }, []);

  const refreshProfile = useCallback(async () => {
    if (!account) return;
    const r = await api.get(`/explorers/${account}`);
    setProfile(r.data);
  }, [account]);

  // Register (acquire) a sector. Real on-chain USDC payment if treasury configured.
  const registerSector = useCallback(async (sector) => {
    if (!account) {
      await connect();
      return { ok: false, needAuth: true };
    }
    let txHash = null;
    let simulated = true;
    const price = settings?.price_usdc ?? 10;
    const value = parseUnits(String(price), 6);

    const hasContract = settings?.erc721_contract_address && window.ethereum;

    if (hasContract) {
      // On-chain mint: buyer approves USDC then the contract pulls payment and forwards it to the treasury.
      try {
        await ensurePolygon();
        const provider = new BrowserProvider(window.ethereum);
        const signer = await provider.getSigner();
        const usdc = new Contract(settings.usdc_contract_address, USDC_ABI, signer);
        const nft = new Contract(settings.erc721_contract_address, ERC721_ABI, signer);
        const tokenId = sector.token_id || sector.tokenId;
        toast.info("Approve USDC in MetaMask", { description: `${price} USDC on Polygon` });
        const ap = await usdc.approve(settings.erc721_contract_address, value);
        await ap.wait();
        toast.info("Confirm registration in MetaMask");
        const tx = await nft.registerSector(tokenId);
        await tx.wait();
        txHash = tx.hash;
        simulated = false;
      } catch (e) {
        toast.error("Registration failed", { description: e.shortMessage || e.message });
        return { ok: false, error: e };
      }
    }

    try {
      const r = await api.post("/register-sector", {
        wallet_address: account,
        sector_id: sector.sector_id || sector.sectorId,
        tx_hash: txHash,
        payment_simulated: simulated,
      });
      await refreshProfile();
      return { ok: true, simulated, data: r.data };
    } catch (e) {
      toast.error("Registration failed", { description: e.response?.data?.detail || e.message });
      return { ok: false, error: e };
    }
  }, [account, settings, connect, refreshProfile]);

  return (
    <WalletContext.Provider
      value={{ account, profile, settings, connecting, connect, disconnect, refreshProfile, registerSector, ensurePolygon }}
    >
      {children}
    </WalletContext.Provider>
  );
}
